<script>
import { reactive } from 'vue'

// Open dialogs in the order they opened: a later one lies above an earlier one (a confirmation
// asked from inside a dialog is never hidden under it), and Escape closes only the top one.
const stack = reactive([])
let next = 0
</script>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue'
import { closesOnBack } from '@/lib/backCloses.js'
import { takeLayer } from '@/lib/layers.js'

const props = defineProps({
  open: Boolean,
  title: { type: String, default: '' },
  size: { type: String, default: 'md' }, // sm | md | lg | xl | full
  closable: { type: Boolean, default: true },
})
const emit = defineEmits(['close'])
const panel = ref(null)
const id = ++next
// Its layer among everything open over the page, taken as it opens (lib/layers.js).
const layer = ref(null)
const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl', full: 'max-w-[min(96vw,1400px)]', screen: 'max-w-none h-[100dvh] sm:max-h-[100dvh] sm:rounded-none' }

function onKey(e) {
  if (e.key === 'Escape' && props.open && props.closable && stack[stack.length - 1] === id) emit('close')
}
// Back on a phone closes the dialog (one that can be closed), not the page under it.
let release = null
const leave = () => { const i = stack.indexOf(id); if (i >= 0) stack.splice(i, 1); release?.(); release = null; layer.value?.release(); layer.value = null }
onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => { document.removeEventListener('keydown', onKey); leave() })
watch(() => props.open, async (open) => {
  leave()
  if (open) {
    stack.push(id)
    layer.value = takeLayer()
    if (props.closable) release = closesOnBack(() => emit('close'))
    await nextTick()
    panel.value?.querySelector('input, select, textarea, [contenteditable="true"], button:not([data-close])')?.focus()
  }
}, { immediate: true })
</script>

<template>
  <Teleport to="body">
    <Transition enter-active-class="transition duration-150" enter-from-class="opacity-0" leave-active-class="transition duration-100" leave-to-class="opacity-0">
      <div v-if="open" :class="['fixed inset-0 flex items-end justify-center bg-slate-900/40 p-0 sm:items-center', size === 'screen' ? '' : 'sm:p-4']" :style="{ zIndex: layer?.z ?? 50 }" @mousedown.self="closable && emit('close')">
        <div ref="panel" role="dialog" aria-modal="true" :aria-label="title" :class="['surface flex max-h-[100dvh] w-full flex-col rounded-t-2xl shadow-2xl sm:max-h-[90vh] sm:rounded-2xl', widths[size] || widths.md]">
          <header v-if="title || $slots.header" class="flex items-center gap-3 border-b border-default px-5 py-3">
            <slot name="header"><h2 class="min-w-0 flex-1 truncate text-base font-semibold">{{ title }}</h2></slot>
            <button v-if="closable" data-close type="button" class="rounded-md p-1.5 text-faint hover:bg-[var(--surface-3)]" :aria-label="$t('common.close')" @click="emit('close')">
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </header>
          <div class="min-h-0 flex-1 overflow-y-auto px-5 py-4"><slot /></div>
          <footer v-if="$slots.footer" class="flex flex-wrap items-center justify-end gap-2 border-t border-default px-5 pt-3 safe-bottom-pad"><slot name="footer" /></footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
